import { IImageLibraryDto } from "@kuika/kuika-cl-model";
import { IScreenCanvasSize, IWatermarkOptions } from "@kuika/kuika-cl-model/src/models/screen-canvas-sizes";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ImageLibraryService } from "../../../../services/image-library-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import ImagePicker from "../styling-panel/styling-color-picker/image-picker";
import PropertyRowIcon from "./property-row-icon";
import WatermarkColorPicker from "./watermark-color-picker";
import "./watermark-settings-panel.scss";

interface IProps extends IWithSDProps {}

const DEFAULT_WATERMARK: IWatermarkOptions = {
  enabled: false,
  type: "text",
  text: "WATERMARK",
  fontSize: 48,
  fontFamily: "poppins_regular",
  fontColor: "#CCCCCC",
  opacity: 0.3,
  rotation: -45,
  bold: false,
  italic: false,
  position: "center",
  customX: 0,
  customY: 0,
  tileRepeat: false,
  tileSpacingX: 200,
  tileSpacingY: 200,
  imageResourceId: "",
  imageScale: 0.5,
  imageFit: "contain",
  imagePosition: "center"
};

const FONT_FAMILY_OPTIONS = [
  { value: "poppins_regular", label: "Poppins" },
  { value: "roboto_regular", label: "Roboto" },
  { value: "montserrat_regular", label: "Montserrat" },
  { value: "calibri_regular", label: "Calibri" },
  { value: "open_sans_regular", label: "Open Sans" },
  { value: "lato_regular", label: "Lato" },
  { value: "verdana_regular", label: "Verdana" },
  { value: "tahoma_regular", label: "Tahoma" }
];

const TYPE_OPTIONS = [
  { value: "text", label: "Text" },
  { value: "image", label: "Image" }
];

const POSITION_OPTIONS = [
  { value: "center", label: "Center" },
  { value: "custom", label: "Custom" }
];

const WatermarkSettingsPanelInner: React.FC<IProps> = ({ sd }) => {
  const screenCanvasSizes = sd.project?.screenCanvasSizes;
  const screen = useMemo(() => screenCanvasSizes?.screens?.find((s) => s.id === sd.sdId), [screenCanvasSizes, sd.sdId]);
  const watermark = useMemo(() => ({ ...DEFAULT_WATERMARK, ...screen?.watermark }), [screen?.watermark]);

  // Local state for text inputs so we only commit on blur
  const [localText, setLocalText] = useState<string | undefined>(undefined);
  const [localFontSize, setLocalFontSize] = useState<string | undefined>(undefined);
  const [localOpacity, setLocalOpacity] = useState<string | undefined>(undefined);
  const [localRotation, setLocalRotation] = useState<string | undefined>(undefined);
  const [localCustomX, setLocalCustomX] = useState<string | undefined>(undefined);
  const [localCustomY, setLocalCustomY] = useState<string | undefined>(undefined);
  const [localTileSpacingX, setLocalTileSpacingX] = useState<string | undefined>(undefined);
  const [localTileSpacingY, setLocalTileSpacingY] = useState<string | undefined>(undefined);
  const [localImageScale, setLocalImageScale] = useState<string | undefined>(undefined);

  // Image library state
  const [allImage, setAllImage] = useState<IImageLibraryDto[]>([]);
  const [isImageLoading, setIsImageLoading] = useState(false);

  const getImageLibrary = useCallback(async () => {
    setIsImageLoading(true);
    try {
      const res = await ImageLibraryService.GetAll();
      if (res && res.status === 200) {
        setAllImage(res.data);
      }
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      setIsImageLoading(false);
    }
  }, []);

  useEffect(() => {
    if (watermark.type === "image") {
      getImageLibrary();
    }
  }, [watermark.type, getImageLibrary]);

  const updateIframeWatermark = useCallback((newWatermark: IWatermarkOptions) => {
    requestAnimationFrame(() => {
      const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
      if (!iframe?.contentWindow) return;
      const rootDI = iframe.contentDocument?.getElementById("rootDI");
      if (!rootDI) return;
      const agent = (iframe.contentWindow as any)?.kuika?.activeKuikaReactInstance;
      if (!agent) return;

      // Sync the agent's internal project state so handleDesignTimeCSS reads the correct watermark
      const screenId = agent.state?.screenDesign?.id;
      if (screenId && agent.state?.project?.screenCanvasSizes?.screens) {
        const agentScreen = agent.state.project.screenCanvasSizes.screens.find((s: any) => s.id === screenId);
        if (agentScreen) {
          agentScreen.watermark = newWatermark;
        }
      }

      if (agent.applyWatermarkOverlay) {
        agent.applyWatermarkOverlay(rootDI, { watermark: newWatermark });
      }
    });
  }, []);

  const updateWatermark = useCallback(
    (updates: Partial<IWatermarkOptions>) => {
      const { project, updateProject } = sd;
      const canvasSizes = project?.screenCanvasSizes ?? { screens: [] as IScreenCanvasSize[] };
      if (!canvasSizes.screens) canvasSizes.screens = [];

      const existingScreen = canvasSizes.screens.find((s) => s.id === sd.sdId);
      const newWatermark = { ...DEFAULT_WATERMARK, ...existingScreen?.watermark, ...updates };

      if (existingScreen) {
        existingScreen.watermark = newWatermark;
      } else {
        canvasSizes.screens.push({ id: sd.sdId, watermark: newWatermark });
      }

      updateProject({ ...project, screenCanvasSizes: { ...canvasSizes } });
      updateIframeWatermark(newWatermark);
    },
    [sd, updateIframeWatermark]
  );

  const renderRow = (label: string, content: React.ReactNode, alignLeft?: boolean) => (
    <div className="watermark-panel__row">
      <div className="watermark-panel__row__label">
        <KuikaSmallMediumText style={{ color: "var(--dark-7)" }}>{label}</KuikaSmallMediumText>
      </div>
      <div className={`watermark-panel__row__control${alignLeft ? " watermark-panel__row__control--left" : ""}`}>
        {content}
      </div>
    </div>
  );

  return (
    <KuikaCollapsePanel
      iconPlacement="right"
      className="kuika-right-panel__collapse kuika-right-panel__collapse-lighter kuika-right-panel__collapse--boxed"
      items={[
        {
          key: "watermark-panel",
          label: (
            <>
              <PropertyRowIcon kind="watermark" />
              <KuikaTextMedium>Watermark</KuikaTextMedium>
            </>
          ),
          children: (
            <div className="kuika-right-panel__collapse-body">
              <div className="watermark-panel">
                {renderRow(
                  "Enable",
                  <KuikaSwitch
                    checked={watermark.enabled}
                    onChange={(checked) => updateWatermark({ enabled: checked })}
                  />,
                  true
                )}

                {watermark.enabled && (
                  <>
                    {renderRow(
                      "Type",
                      <KuikaTabs
                        options={TYPE_OPTIONS}
                        value={watermark.type}
                        onChange={(val) => updateWatermark({ type: val as "text" | "image" })}
                        size="sm"
                      />
                    )}

                    {watermark.type === "text" && (
                      <>
                        {renderRow(
                          "Text",
                          <KuikaInput
                            value={localText ?? watermark.text}
                            onChange={(e) => setLocalText(e.target.value)}
                            onBlur={() => {
                              if (localText !== undefined) {
                                updateWatermark({ text: localText });
                                setLocalText(undefined);
                              }
                            }}
                            onPressEnter={() => {
                              if (localText !== undefined) {
                                updateWatermark({ text: localText });
                                setLocalText(undefined);
                              }
                            }}
                            placeholder="Watermark text"
                            size="sm"
                          />
                        )}
                        {renderRow(
                          "Font Size",
                          <KuikaInput
                            type="number"
                            value={localFontSize ?? watermark.fontSize}
                            onChange={(e) => setLocalFontSize(e.target.value)}
                            onBlur={() => {
                              if (localFontSize !== undefined) {
                                updateWatermark({ fontSize: parseInt(localFontSize) || 48 });
                                setLocalFontSize(undefined);
                              }
                            }}
                            onPressEnter={() => {
                              if (localFontSize !== undefined) {
                                updateWatermark({ fontSize: parseInt(localFontSize) || 48 });
                                setLocalFontSize(undefined);
                              }
                            }}
                            min={8}
                            max={200}
                            size="sm"
                          />
                        )}
                        {renderRow(
                          "Font",
                          <KuikaSelectbox
                            value={watermark.fontFamily}
                            onChange={(val) => updateWatermark({ fontFamily: val as string })}
                            options={FONT_FAMILY_OPTIONS}
                            size="sm"
                            placeholder="Select font"
                            overlayClassName="watermark-font-dropdown"
                          />
                        )}
                        {renderRow(
                          "Color",
                          <WatermarkColorPicker
                            color={watermark.fontColor ?? "#CCCCCC"}
                            onChange={(hex) => updateWatermark({ fontColor: hex })}
                          />
                        )}
                        {renderRow(
                          "Bold",
                          <KuikaSwitch
                            checked={watermark.bold}
                            onChange={(checked) => updateWatermark({ bold: checked })}
                          />,
                          true
                        )}
                        {renderRow(
                          "Italic",
                          <KuikaSwitch
                            checked={watermark.italic}
                            onChange={(checked) => updateWatermark({ italic: checked })}
                          />,
                          true
                        )}
                      </>
                    )}

                    {watermark.type === "image" && (
                      <>
                        <div className="watermark-panel__image-picker">
                          <ImagePicker
                            allImage={allImage}
                            isLoading={isImageLoading}
                            getImageLibrary={getImageLibrary}
                            acceptedFileTypes=".jpg,.png,.svg"
                            fileSize={5}
                            selectedImageId={watermark.imageResourceId}
                            onImageSelect={(image) => updateWatermark({ imageResourceId: image.id })}
                            setBgImageNone={() => updateWatermark({ imageResourceId: "" })}
                            selectedFit={watermark.imageFit || "contain"}
                            onFitChange={(fit) => updateWatermark({ imageFit: fit })}
                            selectedPosition={watermark.imagePosition || "center"}
                            onPositionChange={(pos) => updateWatermark({ imagePosition: pos })}
                            hidePosition
                          />
                        </div>
                        {renderRow(
                          "Scale (%)",
                          <KuikaInput
                            type="number"
                            value={localImageScale ?? Math.round((watermark.imageScale ?? 0.5) * 100)}
                            onChange={(e) => setLocalImageScale(e.target.value)}
                            onBlur={() => {
                              if (localImageScale !== undefined) {
                                updateWatermark({
                                  imageScale: Math.min(100, Math.max(1, parseInt(localImageScale) || 50)) / 100
                                });
                                setLocalImageScale(undefined);
                              }
                            }}
                            onPressEnter={() => {
                              if (localImageScale !== undefined) {
                                updateWatermark({
                                  imageScale: Math.min(100, Math.max(1, parseInt(localImageScale) || 50)) / 100
                                });
                                setLocalImageScale(undefined);
                              }
                            }}
                            min={50}
                            max={100}
                            size="sm"
                          />
                        )}
                      </>
                    )}

                    {renderRow(
                      "Opacity (%)",
                      <KuikaInput
                        type="number"
                        value={localOpacity ?? Math.round((watermark.opacity ?? 0.3) * 100)}
                        onChange={(e) => setLocalOpacity(e.target.value)}
                        onBlur={() => {
                          if (localOpacity !== undefined) {
                            updateWatermark({ opacity: Math.min(100, Math.max(0, parseInt(localOpacity) || 0)) / 100 });
                            setLocalOpacity(undefined);
                          }
                        }}
                        onPressEnter={() => {
                          if (localOpacity !== undefined) {
                            updateWatermark({ opacity: Math.min(100, Math.max(0, parseInt(localOpacity) || 0)) / 100 });
                            setLocalOpacity(undefined);
                          }
                        }}
                        min={0}
                        max={100}
                        size="sm"
                      />
                    )}

                    {watermark.type === "text" && (
                      <>
                        {renderRow(
                          "Rotation",
                          <KuikaInput
                            type="number"
                            value={localRotation ?? watermark.rotation}
                            onChange={(e) => setLocalRotation(e.target.value)}
                            onBlur={() => {
                              if (localRotation !== undefined) {
                                updateWatermark({ rotation: parseInt(localRotation) || 0 });
                                setLocalRotation(undefined);
                              }
                            }}
                            onPressEnter={() => {
                              if (localRotation !== undefined) {
                                updateWatermark({ rotation: parseInt(localRotation) || 0 });
                                setLocalRotation(undefined);
                              }
                            }}
                            min={-180}
                            max={180}
                            size="sm"
                          />
                        )}
                      </>
                    )}

                    {!watermark.tileRepeat &&
                      renderRow(
                        "Position",
                        <KuikaTabs
                          options={POSITION_OPTIONS}
                          value={watermark.position}
                          onChange={(val) => updateWatermark({ position: val as "center" | "custom" })}
                          size="sm"
                        />
                      )}

                    {!watermark.tileRepeat && watermark.position === "custom" && (
                      <>
                        {renderRow(
                          "Custom X",
                          <KuikaInput
                            type="number"
                            value={localCustomX ?? watermark.customX}
                            onChange={(e) => setLocalCustomX(e.target.value)}
                            onBlur={() => {
                              if (localCustomX !== undefined) {
                                updateWatermark({ customX: parseFloat(localCustomX) || 0 });
                                setLocalCustomX(undefined);
                              }
                            }}
                            onPressEnter={() => {
                              if (localCustomX !== undefined) {
                                updateWatermark({ customX: parseFloat(localCustomX) || 0 });
                                setLocalCustomX(undefined);
                              }
                            }}
                            size="sm"
                          />
                        )}
                        {renderRow(
                          "Custom Y",
                          <KuikaInput
                            type="number"
                            value={localCustomY ?? watermark.customY}
                            onChange={(e) => setLocalCustomY(e.target.value)}
                            onBlur={() => {
                              if (localCustomY !== undefined) {
                                updateWatermark({ customY: parseFloat(localCustomY) || 0 });
                                setLocalCustomY(undefined);
                              }
                            }}
                            onPressEnter={() => {
                              if (localCustomY !== undefined) {
                                updateWatermark({ customY: parseFloat(localCustomY) || 0 });
                                setLocalCustomY(undefined);
                              }
                            }}
                            size="sm"
                          />
                        )}
                      </>
                    )}

                    {watermark.type === "text" && (
                      <>
                        {renderRow(
                          "Tile Repeat",
                          <KuikaSwitch
                            checked={watermark.tileRepeat}
                            onChange={(checked) => updateWatermark({ tileRepeat: checked })}
                          />,
                          true
                        )}
                        {watermark.tileRepeat && (
                          <>
                            {renderRow(
                              "Spacing X",
                              <KuikaInput
                                type="number"
                                value={localTileSpacingX ?? watermark.tileSpacingX}
                                onChange={(e) => setLocalTileSpacingX(e.target.value)}
                                onBlur={() => {
                                  if (localTileSpacingX !== undefined) {
                                    updateWatermark({
                                      tileSpacingX: Math.max(parseFloat(localTileSpacingX) || 50, 50)
                                    });
                                    setLocalTileSpacingX(undefined);
                                  }
                                }}
                                onPressEnter={() => {
                                  if (localTileSpacingX !== undefined) {
                                    updateWatermark({
                                      tileSpacingX: Math.max(parseFloat(localTileSpacingX) || 50, 50)
                                    });
                                    setLocalTileSpacingX(undefined);
                                  }
                                }}
                                min={50}
                                size="sm"
                              />
                            )}
                            {renderRow(
                              "Spacing Y",
                              <KuikaInput
                                type="number"
                                value={localTileSpacingY ?? watermark.tileSpacingY}
                                onChange={(e) => setLocalTileSpacingY(e.target.value)}
                                onBlur={() => {
                                  if (localTileSpacingY !== undefined) {
                                    updateWatermark({
                                      tileSpacingY: Math.max(parseFloat(localTileSpacingY) || 50, 50)
                                    });
                                    setLocalTileSpacingY(undefined);
                                  }
                                }}
                                onPressEnter={() => {
                                  if (localTileSpacingY !== undefined) {
                                    updateWatermark({
                                      tileSpacingY: Math.max(parseFloat(localTileSpacingY) || 50, 50)
                                    });
                                    setLocalTileSpacingY(undefined);
                                  }
                                }}
                                min={50}
                                size="sm"
                              />
                            )}
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )
        }
      ]}
    />
  );
};

const watermarkSettingsPanel = withSD<{}>(WatermarkSettingsPanelInner);
export { watermarkSettingsPanel as WatermarkSettingsPanel };
